import { useEffect, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import Modal from "../ui/Modal";
import ConfirmDialog from "../ui/ConfirmDialog";
import FormField, { FormActions, fieldInputStyle } from "../ui/FormField";
import {
  createTarefa,
  updateTarefa,
  deleteTarefa,
  TIPO_TAREFA_OPTIONS,
} from "../../services/tarefasService";
import TarefaDespesaActionDialog from "./TarefaDespesaActionDialog";
import {
  listFornecedores,
  tipoFornecedorIcon,
} from "../../services/fornecedoresService";
import FornecedorFormModal from "./FornecedorFormModal";

// PR4 moeda-por-guia: tarefa herda moeda do guia. Form não tem mais campo Moeda.

const EMPTY = {
  titulo: "",
  descricao: "",
  dataVencimento: "",
  tipo: 5,
  fornecedorId: "",
  valor: "",
};

export default function TarefaFormModal({
  open,
  roteiroId,
  tarefa,
  onClose,
  onSaved,
  onDeleted,
}) {
  const { C } = useTheme();
  const isEdit = !!tarefa;
  const [form, setForm] = useState(EMPTY);
  const [fornecedores, setFornecedores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fornecedorModalOpen, setFornecedorModalOpen] = useState(false);
  // Quando o usuário edita tarefa com despesa linkada e remove o valor, precisamos
  // perguntar o que fazer com a despesa antes de mandar o PUT. Guarda o payload pendente.
  const [pendingDespesaAction, setPendingDespesaAction] = useState(null);
  // Idem pra delete — mostra 3 opções em vez de ConfirmDialog simples.
  const [confirmDeleteWithDespesa, setConfirmDeleteWithDespesa] = useState(false);

  useEffect(() => {
    if (!open) return;
    listFornecedores()
      .then(setFornecedores)
      .catch(() => setFornecedores([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (tarefa) {
      setForm({
        titulo: tarefa.titulo ?? "",
        descricao: tarefa.descricao ?? "",
        dataVencimento: tarefa.dataVencimento ?? "",
        tipo: tarefa.tipo ?? 5,
        fornecedorId: tarefa.fornecedorId != null ? String(tarefa.fornecedorId) : "",
        valor: tarefa.valor != null ? String(tarefa.valor) : "",
      });
    } else {
      setForm(EMPTY);
    }
    setError(null);
  }, [open, tarefa]);

  const valorNum = parseFloat(String(form.valor).replace(",", ".")) || 0;
  const vaiGerarDespesa = !isEdit && valorNum > 0;
  const temDespesaLinkada = isEdit && tarefa?.despesaId != null;
  // Tarefa existente + despesa + usuário zerou/limpou valor → precisa escolher
  const vaiRemoverValorComDespesa = temDespesaLinkada && (!form.valor || valorNum <= 0);
  // Tarefa existente sem despesa + valor > 0 → vai gerar despesa novamente
  const vaiGerarDespesaAoEditar = isEdit && !temDespesaLinkada && valorNum > 0;

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function close() {
    if (saving || deleting) return;
    onClose?.();
  }

  function buildPayload(despesaAction) {
    return {
      titulo: form.titulo.trim(),
      descricao: form.descricao.trim() || null,
      dataVencimento: form.dataVencimento || null,
      tipo: Number(form.tipo),
      fornecedorId: form.fornecedorId ? Number(form.fornecedorId) : null,
      valor: form.valor ? valorNum : null,
      // PR4: moeda herdada do guia no backend (TarefasController.Post).
      despesaAction: despesaAction ?? null,
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.titulo.trim()) {
      setError("O título é obrigatório.");
      return;
    }
    // Se está zerando o valor de uma tarefa com despesa linkada, abre modal antes
    if (vaiRemoverValorComDespesa) {
      setPendingDespesaAction({ phase: "choose" });
      return;
    }
    await doSave(null);
  }

  async function doSave(despesaAction) {
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload(despesaAction);
      if (isEdit) {
        await updateTarefa(roteiroId, tarefa.id, payload);
      } else {
        await createTarefa(roteiroId, payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.join?.(" ") ||
          "Erro ao salvar tarefa."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(despesaAction) {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteTarefa(roteiroId, tarefa.id, despesaAction);
      setConfirmDelete(false);
      setConfirmDeleteWithDespesa(false);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao excluir tarefa.");
      setConfirmDelete(false);
      setConfirmDeleteWithDespesa(false);
    } finally {
      setDeleting(false);
    }
  }

  function requestDelete() {
    // Tarefa com despesa linkada → modal de 3 opções; sem → confirm simples
    if (temDespesaLinkada) {
      setConfirmDeleteWithDespesa(true);
    } else {
      setConfirmDelete(true);
    }
  }

  const inputStyle = fieldInputStyle(C);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? "Editar Tarefa" : "Nova Tarefa"}
        subtitle={isEdit ? tarefa?.titulo : "Checklist operacional do roteiro"}
        width={560}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <FormField label="Título" required span={2}>
              <input
                autoFocus
                value={form.titulo}
                onChange={(e) => update("titulo", e.target.value)}
                style={inputStyle}
                placeholder="Ex: Comprar ingressos Bernina Express"
              />
            </FormField>

            <FormField label="Tipo" required>
              <select
                value={form.tipo}
                onChange={(e) => update("tipo", Number(e.target.value))}
                style={inputStyle}
              >
                {TIPO_TAREFA_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.icon} {t.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Data de vencimento">
              <input
                type="date"
                value={form.dataVencimento}
                onChange={(e) => update("dataVencimento", e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="Fornecedor" span={2}>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={form.fornecedorId}
                  onChange={(e) => update("fornecedorId", e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">(Sem fornecedor)</option>
                  {fornecedores.map((f) => (
                    <option key={f.id} value={f.id}>
                      {tipoFornecedorIcon(f.tipo)} {f.nome}
                      {f.cidade ? ` — ${f.cidade}` : ""}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setFornecedorModalOpen(true)}
                  style={{
                    padding: "11px 14px",
                    borderRadius: 10,
                    border: `1px solid ${C.accent}`,
                    background: "transparent",
                    color: C.accent,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  + Novo
                </button>
              </div>
            </FormField>

            <FormField label="Valor" span={2}>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.valor}
                onChange={(e) => update("valor", e.target.value)}
                style={inputStyle}
                placeholder="0,00"
              />
            </FormField>

            <FormField label="Descrição" span={2}>
              <textarea
                value={form.descricao}
                onChange={(e) => update("descricao", e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
                placeholder="Detalhes, link de confirmação, código de reserva..."
              />
            </FormField>
          </div>

          {(vaiGerarDespesa || vaiGerarDespesaAoEditar) && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.accentDim,
                borderLeft: `3px solid ${C.accent}`,
                borderRadius: 6,
                fontSize: 11,
                color: C.accent,
                fontWeight: 500,
              }}
            >
              💡 Com valor &gt; 0, será criada uma despesa pendente automaticamente
              no Financeiro quando você salvar.
            </div>
          )}

          {temDespesaLinkada && !vaiRemoverValorComDespesa && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.bg,
                borderLeft: `3px solid ${C.accent}`,
                borderRadius: 6,
                fontSize: 11,
                color: C.textMuted,
              }}
            >
              🔗 Esta tarefa tem uma despesa vinculada no Financeiro. Alterações de
              valor, fornecedor, vencimento e tipo serão propagadas automaticamente.
            </div>
          )}

          {vaiRemoverValorComDespesa && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.redDim,
                borderLeft: `3px solid ${C.red}`,
                borderRadius: 6,
                fontSize: 11,
                color: C.red,
                fontWeight: 500,
              }}
            >
              ⚠️ Você removeu o valor. Ao salvar, vamos perguntar o que fazer com a
              despesa vinculada.
            </div>
          )}

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.redDim,
                color: C.red,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}

          <FormActions
            onCancel={close}
            onDelete={isEdit ? requestDelete : undefined}
            saving={saving}
            submitLabel={isEdit ? "Salvar alterações" : "Criar tarefa"}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir tarefa?"
        message={`Tem certeza que deseja excluir "${tarefa?.titulo}"?`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => handleDelete(null)}
      />

      {/* Modal 3 opções: deletar tarefa + cancelar despesa / desvincular / cancelar */}
      <TarefaDespesaActionDialog
        open={confirmDeleteWithDespesa}
        mode="delete"
        tarefaTitulo={tarefa?.titulo}
        loading={deleting}
        onCancel={() => setConfirmDeleteWithDespesa(false)}
        onChoose={(action) => handleDelete(action)}
      />

      {/* Modal 3 opções ao remover valor de tarefa com despesa linkada */}
      <TarefaDespesaActionDialog
        open={pendingDespesaAction?.phase === "choose"}
        mode="remove-value"
        tarefaTitulo={tarefa?.titulo}
        loading={saving}
        onCancel={() => setPendingDespesaAction(null)}
        onChoose={async (action) => {
          setPendingDespesaAction(null);
          await doSave(action);
        }}
      />

      <FornecedorFormModal
        open={fornecedorModalOpen}
        fornecedor={null}
        onClose={() => setFornecedorModalOpen(false)}
        onCreated={(created) => {
          setFornecedores((curr) => [...curr, created].sort((a, b) => a.nome.localeCompare(b.nome)));
          setForm((f) => ({ ...f, fornecedorId: String(created.id) }));
        }}
      />
    </>
  );
}
